import { z } from 'zod';
/**
 * SCIM 2.0 provisioning (RFC 7643 and RFC 7644; docs/adr/0055-sso-and-scim.md). The identity provider creates,
 * updates and deactivates members with the workspace's bearer token. Users and a minimal Groups resource; inputs
 * accept the extra attributes Okta and Entra ID send, and ignore what Chapsi does not store.
 */
export const SCIM_USER_SCHEMA = 'urn:ietf:params:scim:schemas:core:2.0:User';
export const SCIM_GROUP_SCHEMA = 'urn:ietf:params:scim:schemas:core:2.0:Group';
export const SCIM_LIST_SCHEMA = 'urn:ietf:params:scim:api:messages:2.0:ListResponse';
export const SCIM_PATCH_SCHEMA = 'urn:ietf:params:scim:api:messages:2.0:PatchOp';
export const SCIM_ERROR_SCHEMA = 'urn:ietf:params:scim:api:messages:2.0:Error';
export const SCIM_SPC_SCHEMA = 'urn:ietf:params:scim:schemas:core:2.0:ServiceProviderConfig';
/** The most resources one list page returns. */
export const SCIM_MAX_PAGE = 100;
const Text = (max) => z.string().max(max);
export const ScimUserInput = z.looseObject({
    schemas: z.array(z.string().max(200)).max(10).optional(),
    userName: Text(254).min(3),
    externalId: Text(200).optional(),
    displayName: Text(200).optional(),
    name: z.looseObject({ formatted: Text(200).optional(), givenName: Text(100).optional(), familyName: Text(100).optional() }).optional(),
    emails: z.array(z.looseObject({ value: Text(254), primary: z.boolean().optional(), type: Text(50).optional() })).max(10).optional(),
    // Entra ID sends "True"/"False" strings in some requests.
    active: z.union([z.boolean(), z.enum(['true', 'false', 'True', 'False'])]).optional(),
});
export const ScimUser = z.object({
    schemas: z.array(z.string()),
    id: z.uuid(),
    externalId: z.string().optional(),
    userName: z.string(),
    displayName: z.string(),
    name: z.object({ formatted: z.string() }),
    emails: z.array(z.object({ value: z.string(), primary: z.boolean(), type: z.string() })),
    active: z.boolean(),
    meta: z.object({ resourceType: z.literal('User'), created: z.string(), lastModified: z.string(), location: z.string() }),
});
export const ScimPatchOp = z.looseObject({
    schemas: z.array(z.string().max(200)).max(5).optional(),
    Operations: z
        .array(z.looseObject({
        // Entra ID capitalises ops ("Replace", "Add").
        op: z.string().max(20).transform((v) => v.toLowerCase()).pipe(z.enum(['add', 'replace', 'remove'])),
        path: Text(300).optional(),
        value: z.unknown().optional(),
    }))
        .min(1)
        .max(100),
});
export const ScimListQuery = z.object({
    filter: Text(500).optional(),
    startIndex: z.coerce.number().int().min(1).max(1_000_000).optional(),
    count: z.coerce.number().int().min(0).max(1000).optional(),
});
export const ScimGroupInput = z.looseObject({
    schemas: z.array(z.string().max(200)).max(10).optional(),
    displayName: Text(200).min(1),
    externalId: Text(200).optional(),
    members: z.array(z.looseObject({ value: Text(100) })).max(10_000).optional(),
});
export const ScimGroup = z.object({
    schemas: z.array(z.string()),
    id: z.uuid(),
    externalId: z.string().optional(),
    displayName: z.string(),
    members: z.array(z.object({ value: z.string(), display: z.string().optional() })),
    meta: z.object({ resourceType: z.literal('Group'), created: z.string(), lastModified: z.string(), location: z.string() }),
});
const list = (item) => z.object({
    schemas: z.array(z.string()),
    totalResults: z.number().int(),
    startIndex: z.number().int(),
    itemsPerPage: z.number().int(),
    Resources: z.array(item),
});
export const ScimUserList = list(ScimUser);
export const ScimGroupList = list(ScimGroup);
export const ScimServiceProviderConfig = z.object({
    schemas: z.array(z.string()),
    patch: z.object({ supported: z.boolean() }),
    bulk: z.object({ supported: z.boolean(), maxOperations: z.number(), maxPayloadSize: z.number() }),
    filter: z.object({ supported: z.boolean(), maxResults: z.number() }),
    changePassword: z.object({ supported: z.boolean() }),
    sort: z.object({ supported: z.boolean() }),
    etag: z.object({ supported: z.boolean() }),
    authenticationSchemes: z.array(z.object({ type: z.string(), name: z.string(), description: z.string(), primary: z.boolean() })),
});
/** RFC 7644 §3.12 error body. */
export const ScimErrorBody = z.object({
    schemas: z.array(z.string()),
    status: z.string(),
    scimType: z.string().optional(),
    detail: z.string(),
});
/** A DELETE answers 204 with no body. */
export const ScimNoContent = z.object({});
//# sourceMappingURL=scim.js.map